import { IEnvironment } from '@novu/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import { RiEyeLine, RiEyeOffLine, RiLoopRightFill } from 'react-icons/ri';
import {
  getOutboundWebhookSettings,
  OutboundWebhookSettings,
  rotateOutboundWebhookSecret,
  testOutboundWebhook,
  updateOutboundWebhookSettings,
} from '@/api/outbound-webhook-settings';
import { Button } from '@/components/primitives/button';
import { Card, CardContent, CardHeader } from '@/components/primitives/card';
import { Checkbox } from '@/components/primitives/checkbox';
import { CopyButton } from '@/components/primitives/copy-button';
import { Input } from '@/components/primitives/input';
import { Skeleton } from '@/components/primitives/skeleton';
import { showErrorToast, showSuccessToast } from '@/components/primitives/sonner-helpers';
import { Switch } from '@/components/primitives/switch';

const QUERY_KEY = 'outboundWebhookSettings';

/**
 * Where this environment posts signed message status events (sent, delivered, read, failed).
 * Self-hosted replacement for the Cloud webhook portal.
 */
export function StatusWebhookCard({ environment, canEdit }: { environment: IEnvironment; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const queryKey = [QUERY_KEY, environment._id];
  const settingsQuery = useQuery({
    queryKey,
    queryFn: () => getOutboundWebhookSettings({ environment }),
  });
  const settings = settingsQuery.data;

  const [url, setUrl] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [events, setEvents] = useState<string[]>([]);
  const [showSecret, setShowSecret] = useState(false);
  const fieldId = useId();

  useEffect(() => {
    if (!settings) return;
    setUrl(settings.url ?? '');
    setEnabled(settings.enabled);
    setEvents(settings.events);
  }, [settings]);

  const onSaved = (saved: OutboundWebhookSettings) => queryClient.setQueryData(queryKey, saved);
  const save = useMutation({
    mutationFn: () => updateOutboundWebhookSettings({ environment, settings: { url, enabled, events } }),
    onSuccess: (saved) => {
      onSaved(saved);
      showSuccessToast('Status webhook saved. It applies within 30 seconds.');
    },
    onError: (e: Error) => showErrorToast(e.message || 'Failed to save the status webhook'),
  });
  const rotate = useMutation({
    mutationFn: () => rotateOutboundWebhookSecret({ environment }),
    onSuccess: (saved) => {
      onSaved(saved);
      showSuccessToast('Signing secret rotated. Update it on the receiving system.');
    },
    onError: (e: Error) => showErrorToast(e.message || 'Failed to rotate the secret'),
  });
  const sendTest = useMutation({
    mutationFn: () => testOutboundWebhook({ environment }),
    onSuccess: (result) =>
      result.ok
        ? showSuccessToast(`Test event delivered (HTTP ${result.statusCode})`)
        : showErrorToast(`Test event failed: ${result.error ?? `HTTP ${result.statusCode}`}`),
    onError: (e: Error) => showErrorToast(e.message || 'Failed to send the test event'),
  });

  const toggleEvent = (event: string, checked: boolean) =>
    setEvents((current) => (checked ? [...current, event] : current.filter((item) => item !== event)));
  const secret = settings?.secret ?? '';

  return (
    <Card className="w-full overflow-hidden shadow-none">
      <CardHeader>
        Status Webhook
        <p className="text-foreground-500 mt-1 text-xs font-normal">
          Posts message status events to your HTTPS endpoint. Each request carries <code>X-Webhook-Event</code>,{' '}
          <code>X-Webhook-Timestamp</code> and{' '}
          <code>X-Webhook-Signature: sha256=HMAC_SHA256(secret, timestamp + "." + body)</code>. Failed deliveries are
          retried twice (after 10s and 60s).
        </p>
      </CardHeader>
      <CardContent className="rounded-b-xl border-t bg-white p-4">
        {settingsQuery.isLoading ? (
          <Skeleton className="h-[120px] w-full rounded-lg" />
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-[1fr_400px] items-center gap-3">
              <label htmlFor={`${fieldId}-url`} className="text-foreground-600 text-xs font-medium">
                Endpoint URL
              </label>
              <Input
                id={`${fieldId}-url`}
                placeholder="https://example.com/notification-status"
                value={url}
                disabled={!canEdit}
                onChange={(event) => setUrl(event.target.value)}
              />
            </div>
            <div className="grid grid-cols-[1fr_400px] items-center gap-3">
              <label htmlFor={`${fieldId}-enabled`} className="text-foreground-600 text-xs font-medium">
                Enabled
              </label>
              <Switch id={`${fieldId}-enabled`} checked={enabled} disabled={!canEdit} onCheckedChange={setEnabled} />
            </div>
            <div className="grid grid-cols-[1fr_400px] items-start gap-3">
              <span className="text-foreground-600 text-xs font-medium">
                Events
                <span className="text-foreground-400 block font-normal">
                  None selected sends: {settings?.defaultEvents.join(', ')}
                </span>
              </span>
              <div className="grid grid-cols-2 gap-2">
                {settings?.availableEvents.map((event) => (
                  <label
                    key={event}
                    htmlFor={`${fieldId}-${event}`}
                    className="text-foreground-600 flex items-center gap-2 text-xs"
                  >
                    <Checkbox
                      id={`${fieldId}-${event}`}
                      checked={events.includes(event)}
                      disabled={!canEdit}
                      onCheckedChange={(checked) => toggleEvent(event, checked === true)}
                    />
                    {event}
                  </label>
                ))}
              </div>
            </div>
            {secret && (
              <div className="grid grid-cols-[1fr_400px] items-center gap-3">
                <span className="text-foreground-600 text-xs font-medium">Signing secret</span>
                <div className="flex items-center gap-2">
                  <Input
                    className="cursor-default font-mono text-neutral-500!"
                    value={showSecret ? secret : `${'•'.repeat(28)}${secret.slice(-4)}`}
                    readOnly
                    trailingNode={<CopyButton valueToCopy={secret} />}
                    inlineTrailingNode={
                      <button type="button" onClick={() => setShowSecret(!showSecret)}>
                        {showSecret ? (
                          <RiEyeOffLine className="text-text-sub" />
                        ) : (
                          <RiEyeLine className="text-text-sub" />
                        )}
                      </button>
                    }
                  />
                  {canEdit && (
                    <Button
                      size="md"
                      variant="secondary"
                      mode="outline"
                      onClick={() => rotate.mutate()}
                      disabled={rotate.isPending}
                      className="h-[38px] min-w-[38px] p-0"
                      aria-label="Rotate signing secret"
                    >
                      <RiLoopRightFill className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            )}
            {canEdit && (
              <div className="flex justify-end gap-2 border-t pt-4">
                <Button
                  size="sm"
                  variant="secondary"
                  mode="outline"
                  onClick={() => sendTest.mutate()}
                  disabled={!settings?.url}
                  isLoading={sendTest.isPending}
                >
                  Send test event
                </Button>
                <Button size="sm" variant="primary" onClick={() => save.mutate()} isLoading={save.isPending}>
                  Save
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
